import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { STAFF_DEVICE_PEERS } from '@staff-app/graphql-client';
import type { DevicePeerConnection } from '@staff-app/shared-types';
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { formatDateMedium } from '@/lib/format-date';
import { safeDistanceToNow } from '@/lib/safe-format';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Server-side paginated device-peer table. `mdm-devices` where subnet='devices'.
 * Search matches `name` (device UUID) or `vpnIp` via Mongo case-insensitive
 * regex on the backend; typing resets offset to 0.
 */
export function DevicesTable() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);

  // Debounce: mirror the 300ms pattern used elsewhere in scio3. Resetting
  // offset when the search actually changes (not on every keystroke) keeps
  // pagination state aligned with the current result set.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data, loading, error } = useQuery<{ staffDevicePeers: DevicePeerConnection }>(
    STAFF_DEVICE_PEERS,
    {
      variables: {
        filter: search ? { search } : undefined,
        pagination: { offset, limit: PAGE_SIZE },
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const items = data?.staffDevicePeers.items ?? [];
  const total = data?.staffDevicePeers.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + items.length, total);

  return (
    <section className="flex flex-col gap-3" data-testid="devices-table">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{'Devices'}</h2>
        <span className="text-xs text-muted-foreground">
          {total} {'devices'}
        </span>
      </header>

      <Input
        type="search"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder={'Search device UUID or VPN IP…'}
        className="max-w-md"
        data-testid="devices-search"
      />

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{'Device'}</TableHead>
                <TableHead>{'VPN IP'}</TableHead>
                <TableHead>{'Region'}</TableHead>
                <TableHead>{'Created'}</TableHead>
                <TableHead>{'Last handshake'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    {'Loading devices…'}
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    {search ? 'No devices match your search.' : 'No devices yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((peer) => (
                  <TableRow key={peer._id} className="hover:bg-accent/50 transition-colors">
                    <TableCell>
                      <div className="font-medium text-foreground">
                        {peer.deviceName || (
                          <span className="italic text-muted-foreground">{'(unnamed)'}</span>
                        )}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">{peer.name}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{peer.vpnIp}</TableCell>
                    <TableCell>{peer.region}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateMedium(peer.createdAt, '—')}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {safeDistanceToNow(peer.lastHandshakeAt, { addSuffix: true }, '—')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <footer className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {'Showing'} {rangeStart}-{rangeEnd} {'of'} {total}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            data-testid="devices-prev"
          >
            {'Prev'}
          </Button>
          <span className="text-xs">
            {'Page'} {currentPage} {'of'} {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + items.length >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            data-testid="devices-next"
          >
            {'Next'}
          </Button>
        </div>
      </footer>
    </section>
  );
}
